import React, { useState, useEffect, useCallback, useRef } from 'react';
import { restaurantApi, portalApi, API_HOST } from '../../services/api';
import toast from 'react-hot-toast';
import { QRCodeSVG } from 'qrcode.react';
import { useT } from '../../i18n';

const imgUrl = (path) => path ? `${API_HOST}${path}` : null;

const CUISINES = [
  { value: 'pizza',    label: '🍕 Pizza' },
  { value: 'sushi',    label: '🍣 Sushi' },
  { value: 'burger',   label: '🍔 Burger' },
  { value: 'cafe',     label: '☕ Kafe' },
  { value: 'kebab',    label: '🥙 Kebab' },
  { value: 'seafood',  label: '🦞 Dəniz məhsulları' },
  { value: 'dessert',  label: '🍰 Desert' },
  { value: 'georgian', label: '🫕 Gürcü mətbəxi' },
  { value: 'chinese',  label: '🥢 Çin mətbəxi' },
  { value: 'local',    label: '🇦🇿 Azərbaycan mətbəxi' },
];

const DAYS = [
  { key: 'mon', label: 'B.e' }, { key: 'tue', label: 'Ç.a' },
  { key: 'wed', label: 'Çər' }, { key: 'thu', label: 'C.a' },
  { key: 'fri', label: 'Cüm' }, { key: 'sat', label: 'Şən' },
  { key: 'sun', label: 'Baz' },
];

const DEFAULT_HOURS = DAYS.reduce((acc, d) => ({
  ...acc, [d.key]: { open: '09:00', close: '22:00', closed: false },
}), {});

function StarRow({ value, onChange, size = 24 }) {
  return (
    <div style={{ display: 'flex', gap: 4 }}>
      {[1,2,3,4,5].map(n => (
        <span
          key={n}
          onClick={() => onChange && onChange(n)}
          style={{ fontSize: size, cursor: onChange ? 'pointer' : 'default', color: n <= value ? '#f59e0b' : 'var(--border)', lineHeight: 1 }}
        >★</span>
      ))}
    </div>
  );
}

function WorkingHoursEditor({ value, onChange }) {
  const hours = value || DEFAULT_HOURS;
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginTop: 8 }}>
      {DAYS.map(({ key, label }) => {
        const day = hours[key] || { open: '09:00', close: '22:00', closed: false };
        return (
          <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ width: 36, fontSize: 12, fontWeight: 700, color: 'var(--text2)' }}>{label}</span>
            <input type="checkbox" checked={!day.closed}
              onChange={e => onChange({ ...hours, [key]: { ...day, closed: !e.target.checked } })}
              style={{ accentColor: 'var(--primary)', width: 16, height: 16, cursor: 'pointer' }} />
            {!day.closed ? (
              <>
                <input type="time" value={day.open}
                  onChange={e => onChange({ ...hours, [key]: { ...day, open: e.target.value } })}
                  style={S.timeInput} />
                <span style={{ color: 'var(--text3)', fontSize: 12 }}>—</span>
                <input type="time" value={day.close}
                  onChange={e => onChange({ ...hours, [key]: { ...day, close: e.target.value } })}
                  style={S.timeInput} />
              </>
            ) : <span style={{ fontSize: 12, color: 'var(--danger)', marginLeft: 4 }}>Bağlı</span>}
          </div>
        );
      })}
    </div>
  );
}

function PriceRangeSelector({ value, onChange }) {
  return (
    <div style={{ display: 'flex', gap: 8 }}>
      {[1,2,3,4].map(n => (
        <button key={n} type="button" onClick={() => onChange(n)} style={{
          background: value === n ? 'var(--primary)' : 'var(--input-bg)',
          border: `1px solid ${value === n ? 'var(--primary)' : 'var(--border)'}`,
          color: value === n ? 'white' : 'var(--text2)',
          borderRadius: 8, padding: '8px 14px', cursor: 'pointer', fontWeight: 700, fontSize: 14,
        }}>{'₼'.repeat(n)}</button>
      ))}
    </div>
  );
}

function ImageUploadBox({ label, currentUrl, onUpload, uploading }) {
  const ref = useRef();
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
      <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
        {currentUrl ? (
          <img src={currentUrl} alt={label} style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 10, border: '1px solid var(--border)', background: 'var(--bg)' }} />
        ) : (
          <div style={{ width: 72, height: 72, borderRadius: 10, border: '2px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text3)', fontSize: 24 }}>📷</div>
        )}
        <div>
          <button type="button" onClick={() => ref.current?.click()} disabled={uploading}
            style={{ background: 'var(--primary)22', color: 'var(--primary)', border: '1px solid var(--primary)44', borderRadius: 8, padding: '7px 16px', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            {uploading ? '⏳ Yüklənir...' : '📁 Şəkil seç'}
          </button>
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 4 }}>Max 5 MB · JPG, PNG, WEBP</div>
        </div>
      </div>
      <input ref={ref} type="file" accept="image/*" style={{ display: 'none' }}
        onChange={e => { const f = e.target.files?.[0]; if (f) { onUpload(f); e.target.value = ''; } }} />
    </div>
  );
}

export default function Restaurants() {
  const trans = useT();
  const [restaurants, setRestaurants]   = useState([]);
  const [loading, setLoading]           = useState(true);
  const [tab, setTab]                   = useState('list');
  const [selected, setSelected]         = useState(null);

  const emptyForm = { name: '', address: '', phone: '', cuisine: '', priceRange: 2, workingHours: DEFAULT_HOURS };
  const [form, setForm]       = useState(emptyForm);
  const [editMode, setEditMode] = useState(false);

  const [categories, setCategories]       = useState([]);
  const [menuLoading, setMenuLoading]     = useState(false);
  const [newCat, setNewCat]               = useState('');
  const [newItem, setNewItem]             = useState({ name: '', price: '', description: '', preparationMinutes: '', categoryId: '' });
  const [newItemFile, setNewItemFile]     = useState(null);
  const newItemFileRef                    = useRef();

  const [tables, setTables]               = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [newTable, setNewTable]           = useState('');

  const [reviews, setReviews]             = useState([]);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  const [editReviewId, setEditReviewId]   = useState(null);
  const [editReviewForm, setEditReviewForm] = useState({ rating: 5, comment: '', authorName: '' });

  const [uploadingLogo, setUploadingLogo]   = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const [staffList, setStaffList]           = useState([]);
  const [staffLoading, setStaffLoading]     = useState(false);
  const [staffModal, setStaffModal]         = useState(false);
  const [editingStaff, setEditingStaff]     = useState(null);
  const emptyStaffForm = { name: '', email: '', password: '', role: 'staff' };
  const [staffForm, setStaffForm]           = useState(emptyStaffForm);

  const loadRestaurants = useCallback(async () => {
    setLoading(true);
    try {
      const d = await restaurantApi.list();
      setRestaurants(Array.isArray(d) ? d : (d?.data || []));
    } catch { toast.error('Restoranlar yuklenilmedi'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadRestaurants(); }, [loadRestaurants]);

  const loadMenu = useCallback(async (restaurantId) => {
    setMenuLoading(true);
    try {
      const d = await restaurantApi.getMenu(restaurantId);
      setCategories(d?.categories || []);
    } catch { toast.error('Menyu yuklenilmedi'); }
    finally { setMenuLoading(false); }
  }, []);

  const loadTables = useCallback(async (restaurantId) => {
    setTablesLoading(true);
    try {
      const d = await restaurantApi.getTables(restaurantId);
      setTables(Array.isArray(d) ? d : []);
    } catch { toast.error('Masalar yuklenilmedi'); }
    finally { setTablesLoading(false); }
  }, []);

  const loadReviews = useCallback(async (restaurantId) => {
    setReviewsLoading(true);
    try {
      const d = await restaurantApi.getReviews(restaurantId);
      setReviews(Array.isArray(d) ? d : []);
    } catch { toast.error('Rəylər yuklenilmedi'); }
    finally { setReviewsLoading(false); }
  }, []);

  const loadStaff = useCallback(async (restaurantId) => {
    setStaffLoading(true);
    try { setStaffList(await portalApi.getStaff(restaurantId)); }
    catch { toast.error('Personal yüklənmədi'); }
    finally { setStaffLoading(false); }
  }, []);

  const selectTab = (t, rest = selected) => {
    setTab(t);
    if (t === 'menu'    && rest) loadMenu(rest.id);
    if (t === 'tables'  && rest) loadTables(rest.id);
    if (t === 'reviews' && rest) loadReviews(rest.id);
    if (t === 'staff'   && rest) loadStaff(rest.id);
  };

  const openRestaurant = (r, t) => { setSelected(r); selectTab(t, r); };

  const openEdit = (r) => {
    setSelected(r);
    setEditMode(true);
    setForm({ name: r.name||'', address: r.address||'', phone: r.phone||'', cuisine: r.cuisine||'', priceRange: r.priceRange||2, workingHours: r.workingHours||DEFAULT_HOURS });
    setTab('create');
  };

  const handleCreate = async e => {
    e.preventDefault();
    try {
      if (editMode && selected) {
        const updated = await restaurantApi.update(selected.id, form);
        setSelected(updated);
        setEditMode(false);
        setTab('list');
        setForm(emptyForm);
        toast.success('Restoran yenilendi');
      } else {
        await restaurantApi.create(form);
        toast.success('Restoran elave edildi');
        setTab('list');
        setForm(emptyForm);
        setEditMode(false);
        setSelected(null);
      }
      loadRestaurants();
    } catch { toast.error('Xeta bas verdi'); }
  };

  const handleUploadImage = async (type, file) => {
    if (!file || !selected) return;
    const setUploading = type === 'logo' ? setUploadingLogo : setUploadingCover;
    setUploading(true);
    try {
      const d = await restaurantApi.uploadImage(selected.id, type, file);
      setSelected(d);
      toast.success('Şəkil yükləndi');
      loadRestaurants();
    } catch { toast.error('Şəkil yüklənilmədi'); }
    finally { setUploading(false); }
  };

  const handleAddGallery = async (file) => {
    if (!file || !selected) return;
    try {
      const fd = new FormData(); fd.append('image', file);
      const d = await restaurantApi.addGalleryImage(selected.id, fd);
      setSelected(d); loadRestaurants();
      toast.success('Şəkil əlavə edildi');
    } catch { toast.error('Şəkil əlavə edilmədi'); }
  };

  const handleRemoveGallery = async (imageUrl) => {
    if (!selected || !window.confirm('Bu şəkili silinsin?')) return;
    try {
      const d = await restaurantApi.removeGalleryImage(selected.id, imageUrl);
      setSelected(d); loadRestaurants();
      toast.success('Silindi');
    } catch { toast.error('Silinmədi'); }
  };

  const handleAddCat = async e => {
    e.preventDefault();
    if (!newCat.trim()) return;
    try {
      await restaurantApi.addCategory(selected.id, newCat);
      toast.success('Kateqoriya elave edildi');
      setNewCat('');
      loadMenu(selected.id);
    } catch { toast.error('Xeta bas verdi'); }
  };

  const handleAddItem = async e => {
    e.preventDefault();
    if (!newItem.categoryId) { toast.error('Kateqoriya secin'); return; }
    try {
      const item = await restaurantApi.addItem(newItem.categoryId, {
        name: newItem.name,
        price: parseFloat(newItem.price),
        description: newItem.description || undefined,
        preparationMinutes: newItem.preparationMinutes ? parseInt(newItem.preparationMinutes) : 0,
      });
      if (newItemFile && item?.id) {
        try { await restaurantApi.uploadItemImage(item.id, newItemFile); }
        catch { toast.error('Şəkil yüklenilmedi'); }
      }
      toast.success('Mehsul elave edildi');
      setNewItem({ name: '', price: '', description: '', preparationMinutes: '', categoryId: newItem.categoryId });
      setNewItemFile(null);
      loadMenu(selected.id);
    } catch { toast.error('Xeta bas verdi'); }
  };

  const handleUploadItemImage = async (itemId, file) => {
    try {
      await restaurantApi.uploadItemImage(itemId, file);
      toast.success('Şəkil yüklendi');
      loadMenu(selected.id);
    } catch { toast.error('Xeta'); }
  };

  const handleToggleItem = async (item) => {
    try {
      await restaurantApi.toggleItem(item.id);
      loadMenu(selected.id);
    } catch { toast.error('Xeta bas verdi'); }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Bu mehsulu silmek isteyirsiniz?')) return;
    try { await restaurantApi.deleteItem(itemId); toast.success('Mehsul silindi'); loadMenu(selected.id); }
    catch { toast.error('Xeta bas verdi'); }
  };

  const handleDeleteCategory = async (catId) => {
    if (!window.confirm('Bu kateqoriyanı silmek isteyirsiniz? Bütün mehsullar da silinecek.')) return;
    try { await restaurantApi.deleteCategory(catId); toast.success('Kateqoriya silindi'); loadMenu(selected.id); }
    catch { toast.error('Xeta bas verdi'); }
  };

  const handleAddTable = async e => {
    e.preventDefault();
    if (!newTable.trim()) return;
    try { await restaurantApi.addTable(selected.id, newTable); toast.success('Masa elave edildi'); setNewTable(''); loadTables(selected.id); }
    catch { toast.error('Xeta bas verdi'); }
  };

  const startEditReview = (r) => {
    setEditReviewId(r.id);
    setEditReviewForm({ rating: r.rating, comment: r.comment || '', authorName: r.authorName || '' });
  };

  const handleUpdateReview = async () => {
    try {
      await restaurantApi.updateReview(editReviewId, editReviewForm);
      toast.success('Rəy yenilendi');
      setEditReviewId(null);
      loadReviews(selected.id);
      loadRestaurants();
    } catch { toast.error('Xeta bas verdi'); }
  };

  const handleDeleteReview = async (reviewId) => {
    if (!window.confirm('Bu rəyi silmek isteyirsiniz?')) return;
    try {
      await restaurantApi.deleteReview(reviewId);
      toast.success('Rəy silindi');
      loadReviews(selected.id);
      loadRestaurants();
    } catch { toast.error('Xeta bas verdi'); }
  };

  const openAddStaff = () => { setEditingStaff(null); setStaffForm(emptyStaffForm); setStaffModal(true); };
  const openEditStaff = (s) => { setEditingStaff(s); setStaffForm({ name: s.name, email: s.email, password: '', role: s.role }); setStaffModal(true); };

  const handleSaveStaff = async () => {
    try {
      if (editingStaff) {
        const d = { name: staffForm.name, role: staffForm.role };
        if (staffForm.password) d.password = staffForm.password;
        await portalApi.updateStaff(selected.id, editingStaff.id, d);
        toast.success('İşçi yenilendi');
      } else {
        if (!staffForm.name || !staffForm.email || !staffForm.password) { toast.error('Ad, e-poçt və şifrə lazımdır'); return; }
        await portalApi.createStaff(selected.id, staffForm);
        toast.success('İşçi əlavə edildi');
      }
      setStaffModal(false);
      loadStaff(selected.id);
    } catch (e) { toast.error(e.response?.data?.message || 'Xəta baş verdi'); }
  };

  const handleDeleteStaff = async (staffId) => {
    if (!window.confirm('Bu işçini silinsin?')) return;
    try { await portalApi.deleteStaff(selected.id, staffId); toast.success('Silindi'); loadStaff(selected.id); }
    catch { toast.error('Xəta baş verdi'); }
  };

  const ROLE_LABELS = { admin:'Admin', chef:'Aşpaz', cashier:'Kassir', accountant:'Mühasib', waiter:'Ofisiant', staff:'İşçi' };
  const ROLE_COLORS = { admin:'#00c896', chef:'#f59e0b', cashier:'#4a90e2', accountant:'#8b5cf6', waiter:'#22c55e', staff:'#8ba3cb' };

  return (
    <div>
      <div style={S.header}>
        <div>
          <h1 style={S.title}>{trans('nav_restaurants')}</h1>
          <p style={S.sub}>{trans('rest_sub')}</p>
        </div>
        <div style={S.tabs}>
          {[['list', trans('rest_list')], ['create', editMode ? '✎ Redaktə' : '+ ' + trans('rest_new_short')]].map(([t, l]) => (
            <button key={t} style={{ ...S.tab, ...(tab === t ? S.tabActive : {}) }}
              onClick={() => { if (t === 'list') { setEditMode(false); setForm(emptyForm); } setTab(t); }}>{l}</button>
          ))}
        </div>
      </div>

      {/* ── CREATE / EDIT ─────────────────────────────────────────── */}
      {tab === 'create' && (
        <form onSubmit={handleCreate} style={S.card}>
          <h3 style={S.cardTitle}>{editMode ? `✎ ${selected?.name} — Redaktə` : trans('rest_new')}</h3>

          <div style={S.row3}>
            <Field label={trans('name') + ' *'}>
              <input style={S.input} value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required placeholder={trans('rest_name_ph')} />
            </Field>
            <Field label={trans('address')}>
              <input style={S.input} value={form.address} onChange={e => setForm({ ...form, address: e.target.value })} placeholder="Bakı, Nərimanov..." />
            </Field>
            <Field label={trans('phone')}>
              <input style={S.input} value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} placeholder="+994..." />
            </Field>
          </div>

          <div style={S.row2}>
            <Field label="Mətbəx növü">
              <select style={S.input} value={form.cuisine} onChange={e => setForm({ ...form, cuisine: e.target.value })}>
                <option value="">Seçin...</option>
                {CUISINES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
              </select>
            </Field>
            <Field label="Qiymət səviyyəsi">
              <PriceRangeSelector value={form.priceRange} onChange={v => setForm({ ...form, priceRange: v })} />
            </Field>
          </div>

          <Field label="İş saatları">
            <WorkingHoursEditor value={form.workingHours} onChange={wh => setForm({ ...form, workingHours: wh })} />
          </Field>

          {/* Image upload — only in edit mode (restaurant must exist) */}
          {editMode && selected && (
            <div style={{ marginTop: 20, padding: '20px', background: 'var(--bg)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 16 }}>📸 Şəkillər</div>
              <div style={S.row2}>
                <ImageUploadBox
                  label="Loqo (kiçik kvadrat)"
                  currentUrl={imgUrl(selected.logoUrl)}
                  onUpload={f => handleUploadImage('logo', f)}
                  uploading={uploadingLogo}
                />
                <ImageUploadBox
                  label="Əsas örtük şəkli"
                  currentUrl={imgUrl(selected.coverImageUrl)}
                  onUpload={f => handleUploadImage('cover', f)}
                  uploading={uploadingCover}
                />
              </div>

              {/* Gallery — multiple cover images */}
              <div style={{ marginTop: 20 }}>
                <div style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: 10 }}>Qalereya (çox şəkil)</div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
                  {(selected.galleryImages || []).map((url, i) => (
                    <div key={i} style={{ position: 'relative', width: 90, height: 90 }}>
                      <img src={imgUrl(url)} alt="" style={{ width: 90, height: 90, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                      <button type="button" onClick={() => handleRemoveGallery(url)}
                        style={{ position: 'absolute', top: -6, right: -6, width: 20, height: 20, borderRadius: '50%', background: 'var(--danger)', color: '#fff', border: 'none', cursor: 'pointer', fontSize: 12, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>×</button>
                    </div>
                  ))}
                  <label style={{ width: 90, height: 90, border: '2px dashed var(--border)', borderRadius: 8, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', color: 'var(--text3)', fontSize: 11 }}>
                    <span style={{ fontSize: 24 }}>+</span>
                    <span>Şəkil əlavə et</span>
                    <input type="file" accept="image/*" style={{ display: 'none' }} onChange={e => { const f = e.target.files?.[0]; if (f) { handleAddGallery(f); e.target.value = ''; } }} />
                  </label>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: 'flex', gap: 12, marginTop: 20 }}>
            <button style={S.btn} type="submit">{editMode ? 'Yadda Saxla' : trans('bar_add_btn')}</button>
            {editMode && (
              <button type="button" style={S.cancelBtn} onClick={() => { setEditMode(false); setForm(emptyForm); setTab('list'); }}>Ləğv et</button>
            )}
          </div>
        </form>
      )}

      {/* ── LIST ──────────────────────────────────────────────────── */}
      {tab === 'list' && (
        loading ? <div style={S.center}>{trans('loading')}</div> :
        restaurants.length === 0 ? (
          <div style={S.empty}>
            <div style={{ fontSize: 48, marginBottom: 12 }}>◉</div>
            <div>{trans('rest_empty')}</div>
            <button style={{ ...S.btn, marginTop: 16 }} onClick={() => setTab('create')}>+ {trans('rest_first')}</button>
          </div>
        ) : (
          <div style={S.list}>
            {restaurants.map(r => {
              const cuisine = CUISINES.find(c => c.value === r.cuisine);
              const cover   = imgUrl(r.coverImageUrl) || imgUrl(r.logoUrl);
              return (
                <div key={r.id} style={S.item}>
                  {cover
                    ? <img src={cover} alt={r.name} style={{ width: 56, height: 56, borderRadius: 10, objectFit: 'cover', flexShrink: 0, border: '1px solid var(--border)' }} />
                    : <div style={{ width: 56, height: 56, borderRadius: 10, background: 'var(--bg)', border: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 24, flexShrink: 0 }}>
                        {cuisine?.label?.split(' ')[0] || '🍽'}
                      </div>
                  }
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4, flexWrap: 'wrap' }}>
                      <div style={S.itemName}>{r.name}</div>
                      {cuisine && <span style={S.cuisineTag}>{cuisine.label}</span>}
                      {r.priceRange && <span style={S.priceTag}>{'₼'.repeat(r.priceRange)}</span>}
                      {r.rating > 0 && <span style={S.ratingTag}>⭐ {Number(r.rating).toFixed(1)} ({r.reviewCount})</span>}
                    </div>
                    <div style={S.itemSub}>{r.address || 'Unvan yoxdur'}</div>
                  </div>
                  <div style={S.actions}>
                    <button style={S.editBtn}    onClick={() => openEdit(r)}>✎ Redaktə</button>
                    <button style={S.actionBtn}  onClick={() => openRestaurant(r, 'reviews')}>★ Rəylər</button>
                    <button style={S.actionBtn}  onClick={() => openRestaurant(r, 'menu')}>{trans('rest_menu')}</button>
                    <button style={S.actionBtn}  onClick={() => openRestaurant(r, 'tables')}>{trans('rest_tables')}</button>
                    <button style={{ ...S.actionBtn, color: 'var(--text2)', background: 'var(--bg)' }} onClick={() => openRestaurant(r, 'staff')}>👥 Personal</button>
                  </div>
                </div>
              );
            })}
          </div>
        )
      )}

      {/* ── REVIEWS ───────────────────────────────────────────────── */}
      {tab === 'reviews' && selected && (
        <div>
          <button style={S.back} onClick={() => setTab('list')}>← {trans('back')}</button>
          <h2 style={S.secTitle}>{selected.name} — Rəylər & Reytinqlər</h2>

          {reviewsLoading ? <div style={S.center}>{trans('loading')}</div> : (
            reviews.length === 0 ? (
              <div style={S.empty}><div style={{ fontSize: 36, marginBottom: 12 }}>★</div><div>Hələ rəy yoxdur</div></div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {reviews.map(r => (
                  <div key={r.id} style={S.card}>
                    {editReviewId === r.id ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 4 }}>Rəyi Redaktə Et</div>
                        <Field label="Ad">
                          <input style={S.input} value={editReviewForm.authorName}
                            onChange={e => setEditReviewForm({ ...editReviewForm, authorName: e.target.value })} />
                        </Field>
                        <Field label="Qiymətləndirmə">
                          <StarRow value={editReviewForm.rating} onChange={v => setEditReviewForm({ ...editReviewForm, rating: v })} size={28} />
                        </Field>
                        <Field label="Şərh">
                          <textarea style={{ ...S.input, minHeight: 80, resize: 'vertical' }} value={editReviewForm.comment}
                            onChange={e => setEditReviewForm({ ...editReviewForm, comment: e.target.value })} />
                        </Field>
                        <div style={{ display: 'flex', gap: 8 }}>
                          <button style={S.btn} type="button" onClick={handleUpdateReview}>Yadda Saxla</button>
                          <button style={S.cancelBtn} type="button" onClick={() => setEditReviewId(null)}>Ləğv et</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: 16, alignItems: 'flex-start' }}>
                        <div style={{ width: 44, height: 44, borderRadius: 22, background: 'var(--primary)22', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18, fontWeight: 800, color: 'var(--primary)', flexShrink: 0 }}>
                          {(r.authorName || 'M')[0].toUpperCase()}
                        </div>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
                            <span style={{ fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>{r.authorName || 'Müştəri'}</span>
                            <StarRow value={r.rating} size={14} />
                            <span style={{ fontSize: 12, color: 'var(--text3)', marginLeft: 'auto' }}>
                              {new Date(r.createdAt).toLocaleDateString('az-AZ')}
                            </span>
                          </div>
                          {r.comment && <p style={{ fontSize: 13, color: 'var(--text2)', margin: 0, lineHeight: 1.5 }}>{r.comment}</p>}
                        </div>
                        <div style={{ display: 'flex', gap: 6, flexShrink: 0 }}>
                          <button onClick={() => startEditReview(r)} style={{ ...S.iconBtn, color: 'var(--warning)' }} title="Redaktə">✎</button>
                          <button onClick={() => handleDeleteReview(r.id)} style={{ ...S.iconBtn, color: 'var(--danger)' }} title="Sil">🗑</button>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}

      {/* ── MENU ──────────────────────────────────────────────────── */}
      {tab === 'menu' && selected && (
        <div>
          <button style={S.back} onClick={() => setTab('list')}>← {trans('back')}</button>
          <h2 style={S.secTitle}>{selected.name} — {trans('rest_menu')}</h2>

          <div style={S.card}>
            <h3 style={S.cardTitle}>{trans('rest_add_cat')}</h3>
            <form onSubmit={handleAddCat} style={{ display: 'flex', gap: 12 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder={trans('rest_cat_ph')} value={newCat} onChange={e => setNewCat(e.target.value)} required />
              <button style={S.btn} type="submit">{trans('bar_add_btn')}</button>
            </form>
          </div>

          <div style={S.card}>
            <h3 style={S.cardTitle}>{trans('rest_add_item')}</h3>
            <div style={S.row}>
              <Field label={trans('rest_category') + ' *'}>
                <select style={S.input} value={newItem.categoryId} onChange={e => setNewItem({ ...newItem, categoryId: e.target.value })} required>
                  <option value="">{trans('rest_select')}...</option>
                  {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </Field>
              <Field label={trans('name') + ' *'}>
                <input style={S.input} value={newItem.name} onChange={e => setNewItem({ ...newItem, name: e.target.value })} required placeholder="Marqerita pizza" />
              </Field>
              <Field label={trans('rest_price') + ' (AZN) *'}>
                <input style={S.input} type="number" step="0.01" min="0" value={newItem.price} onChange={e => setNewItem({ ...newItem, price: e.target.value })} required placeholder="12.50" />
              </Field>
              <Field label="Hazırlama (dəq)">
                <input style={S.input} type="number" min="0" value={newItem.preparationMinutes} onChange={e => setNewItem({ ...newItem, preparationMinutes: e.target.value })} placeholder="15" />
              </Field>
            </div>
            <div style={S.row2}>
              <Field label={trans('rest_desc')}>
                <input style={S.input} value={newItem.description} onChange={e => setNewItem({ ...newItem, description: e.target.value })} placeholder={trans('rest_desc_ph')} />
              </Field>
              <Field label="Şəkil (ixtiyari)">
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  {newItemFile && (
                    <img src={URL.createObjectURL(newItemFile)} alt="preview" style={{ width: 44, height: 44, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)' }} />
                  )}
                  <button type="button" onClick={() => newItemFileRef.current?.click()}
                    style={{ background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '9px 14px', fontSize: 13, color: 'var(--text2)', cursor: 'pointer' }}>
                    {newItemFile ? '✓ Şəkil seçildi' : '📁 Şəkil seç'}
                  </button>
                  {newItemFile && <button type="button" onClick={() => setNewItemFile(null)} style={{ background: 'none', border: 'none', color: 'var(--danger)', cursor: 'pointer', fontSize: 16 }}>✕</button>}
                </div>
                <input ref={newItemFileRef} type="file" accept="image/*" style={{ display: 'none' }}
                  onChange={e => { const f = e.target.files?.[0]; if (f) { setNewItemFile(f); e.target.value = ''; } }} />
              </Field>
            </div>
            <button style={{ ...S.btn, marginTop: 12 }} type="button" onClick={handleAddItem}>{trans('rest_add_item_btn')}</button>
          </div>

          {menuLoading ? <div style={S.center}>{trans('loading')}</div> : (
            categories.length === 0 ? (
              <div style={S.empty}><div>{trans('rest_no_cat')}</div></div>
            ) : (
              <div>
                {categories.map(cat => (
                  <div key={cat.id} style={{ ...S.card, marginBottom: 16 }}>
                    <div style={S.catHead}>
                      <span style={S.catName}>{cat.name}</span>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <span style={S.catCount}>{cat.items?.length || 0} məhsul</span>
                        <button onClick={() => handleDeleteCategory(cat.id)} style={S.delBtn} title="Kateqoriyanı sil">🗑</button>
                      </div>
                    </div>
                    {cat.items?.length === 0 ? (
                      <div style={{ color: 'var(--text3)', fontSize: 13, padding: '8px 0' }}>Məhsul yoxdur</div>
                    ) : (
                      <div style={S.itemList}>
                        {cat.items?.map(item => {
                          const itemImgRef = React.createRef();
                          return (
                            <div key={item.id} style={{ ...S.menuItem, opacity: item.isAvailable ? 1 : 0.5 }}>
                              {/* Item image */}
                              {imgUrl(item.imageUrl)
                                ? <img src={imgUrl(item.imageUrl)} alt={item.name} style={{ width: 52, height: 52, objectFit: 'cover', borderRadius: 8, border: '1px solid var(--border)', flexShrink: 0 }} />
                                : <div style={{ width: 52, height: 52, borderRadius: 8, background: 'var(--bg)', border: '1px dashed var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 20, flexShrink: 0 }}>🍽</div>
                              }
                              <div style={{ flex: 1 }}>
                                <div style={{ ...S.menuItemName, display: 'flex', alignItems: 'center', gap: 8 }}>
                                  {item.name}
                                  {!item.isAvailable && <span style={{ fontSize: 10, color: 'var(--danger)', background: 'var(--danger)15', padding: '2px 6px', borderRadius: 10 }}>Deaktiv</span>}
                                  {item.preparationMinutes > 0 && <span style={{ fontSize: 11, color: 'var(--text3)' }}>⏱{item.preparationMinutes}d</span>}
                                </div>
                                {item.description && <div style={S.menuItemDesc}>{item.description}</div>}
                              </div>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                                <div style={S.menuItemPrice}>{Number(item.price).toFixed(2)} AZN</div>
                                {/* Upload item image */}
                                <label title="Şəkil yüklə" style={{ cursor: 'pointer', fontSize: 14, padding: '4px 6px', color: 'var(--text3)', borderRadius: 6, border: '1px solid var(--border)', background: 'var(--input-bg)' }}>
                                  📷
                                  <input type="file" accept="image/*" style={{ display: 'none' }}
                                    onChange={e => { const f = e.target.files?.[0]; if (f) { handleUploadItemImage(item.id, f); e.target.value = ''; } }} />
                                </label>
                                <button onClick={() => handleToggleItem(item)}
                                  style={{ ...S.iconBtn, color: item.isAvailable ? 'var(--success)' : 'var(--text3)' }}
                                  title={item.isAvailable ? 'Deaktiv et' : 'Aktiv et'}>
                                  {item.isAvailable ? '✓' : '○'}
                                </button>
                                <button onClick={() => handleDeleteItem(item.id)} style={{ ...S.iconBtn, color: 'var(--danger)' }} title="Sil">🗑</button>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )
          )}
        </div>
      )}

      {/* ── STAFF ────────────────────────────────────────────────── */}
      {tab === 'staff' && selected && (
        <div>
          <button style={S.back} onClick={() => setTab('list')}>← {trans('back')}</button>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
            <h2 style={S.secTitle}>{selected.name} — 👥 Portal Personalı</h2>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <a href="https://restoran.bakugate.com" target="_blank" rel="noreferrer"
                style={{ ...S.actionBtn, display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, textDecoration: 'none' }}>
                🌐 Portal linkini aç
              </a>
              <button style={S.btn} onClick={openAddStaff}>+ İşçi əlavə et</button>
            </div>
          </div>

          <div style={{ background: 'var(--primary)11', border: '1px solid var(--primary)33', borderRadius: 10, padding: '12px 16px', marginBottom: 16, fontSize: 13, color: 'var(--text2)' }}>
            💡 Portal istifadəçiləri <strong style={{ color: 'var(--primary)' }}>restoran.bakugate.com</strong> ünvanına daxil olub öz rollarına uyğun səhifəni görəcəklər.
          </div>

          {staffLoading ? <div style={S.center}>Yüklənir...</div> : (
            <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' }}>
              {staffList.length === 0 ? (
                <div style={S.empty}>
                  <div style={{ fontSize: 36, marginBottom: 12 }}>👥</div>
                  <div>Hələ portal işçisi yoxdur</div>
                  <button style={{ ...S.btn, marginTop: 12 }} onClick={openAddStaff}>+ İlk işçini əlavə et</button>
                </div>
              ) : (
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ background: 'var(--bg)' }}>
                      {['Ad', 'E-poçt', 'Rol', 'Status', 'Qeydiyyat', ''].map(h => (
                        <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, fontWeight: 700, color: 'var(--text3)', textTransform: 'uppercase', letterSpacing: '0.06em', borderBottom: '1px solid var(--border)' }}>{h}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {staffList.map(s => (
                      <tr key={s.id} style={{ borderBottom: '1px solid var(--border)' }}>
                        <td style={{ padding: '12px 14px', fontWeight: 700, color: 'var(--text)', fontSize: 14 }}>{s.name}</td>
                        <td style={{ padding: '12px 14px', color: 'var(--text2)', fontSize: 13 }}>{s.email}</td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{ background: (ROLE_COLORS[s.role] || '#8ba3cb') + '22', color: ROLE_COLORS[s.role] || '#8ba3cb', border: '1px solid ' + (ROLE_COLORS[s.role] || '#8ba3cb') + '44', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 700 }}>
                            {ROLE_LABELS[s.role] || s.role}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', fontSize: 13, color: s.isActive ? 'var(--success)' : 'var(--danger)', fontWeight: 700 }}>
                          {s.isActive ? '● Aktiv' : '○ Deaktiv'}
                        </td>
                        <td style={{ padding: '12px 14px', color: 'var(--text3)', fontSize: 12 }}>
                          {new Date(s.createdAt).toLocaleDateString('az-AZ')}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <div style={{ display: 'flex', gap: 6 }}>
                            <button onClick={() => openEditStaff(s)} style={{ ...S.iconBtn, background: 'var(--warning)22', color: 'var(--warning)', padding: '5px 10px', borderRadius: 7 }}>✎ Düzəlt</button>
                            <button onClick={() => handleDeleteStaff(s.id)} style={{ ...S.iconBtn, background: 'var(--danger)15', color: 'var(--danger)', padding: '5px 10px', borderRadius: 7 }}>🗑 Sil</button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          )}

          {/* Staff modal */}
          {staffModal && (
            <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
              <div style={{ background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 14, padding: 28, width: '100%', maxWidth: 460 }}>
                <h3 style={{ ...S.cardTitle, marginBottom: 20 }}>{editingStaff ? '✎ İşçini Düzəlt' : '+ Yeni Portal İşçisi'}</h3>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                  <Field label="Ad Soyad *">
                    <input style={S.input} value={staffForm.name} onChange={e => setStaffForm({ ...staffForm, name: e.target.value })} placeholder="Məs: Elçin Həsənov" />
                  </Field>
                  {!editingStaff && (
                    <Field label="E-poçt *">
                      <input style={S.input} type="email" value={staffForm.email} onChange={e => setStaffForm({ ...staffForm, email: e.target.value })} placeholder="elcin@restoran.az" />
                    </Field>
                  )}
                  <Field label={editingStaff ? 'Yeni şifrə (boş = dəyişməz)' : 'Şifrə *'}>
                    <input style={S.input} type="password" value={staffForm.password} onChange={e => setStaffForm({ ...staffForm, password: e.target.value })} placeholder="••••••••" />
                  </Field>
                  <Field label="Rol">
                    <select style={S.input} value={staffForm.role} onChange={e => setStaffForm({ ...staffForm, role: e.target.value })}>
                      <option value="admin">👑 Admin</option>
                      <option value="chef">👨‍🍳 Aşpaz</option>
                      <option value="cashier">💰 Kassir</option>
                      <option value="accountant">📊 Mühasib</option>
                      <option value="waiter">🤵 Ofisiant</option>
                      <option value="staff">👤 Ümumi işçi</option>
                    </select>
                  </Field>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 20, justifyContent: 'flex-end' }}>
                  <button style={S.cancelBtn} type="button" onClick={() => setStaffModal(false)}>Ləğv et</button>
                  <button style={S.btn} type="button" onClick={handleSaveStaff}>Yadda saxla</button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── TABLES ────────────────────────────────────────────────── */}
      {tab === 'tables' && selected && (
        <div>
          <button style={S.back} onClick={() => setTab('list')}>← {trans('back')}</button>
          <h2 style={S.secTitle}>{selected.name} — {trans('rest_tables')} & QR</h2>

          <div style={S.card}>
            <form onSubmit={handleAddTable} style={{ display: 'flex', gap: 12 }}>
              <input style={{ ...S.input, flex: 1 }} placeholder={trans('rest_table_ph')} value={newTable} onChange={e => setNewTable(e.target.value)} required />
              <button style={S.btn} type="submit">{trans('rest_add_table')}</button>
            </form>
          </div>

          {tablesLoading ? <div style={S.center}>{trans('loading')}</div> : (
            tables.length === 0 ? (
              <div style={S.empty}><div>{trans('rest_no_tables')}</div></div>
            ) : (
              <div style={S.tableGrid}>
                {tables.map(t => {
                  const webBase = process.env.REACT_APP_WEB_URL || 'https://qr.bakugate.com';
                  const menuUrl = `${webBase}/menu/${t.id}`;
                  return (
                    <div key={t.id} style={S.tableCard}>
                      <div style={S.tableNum}>{trans('rest_table')} {t.tableNumber}</div>
                      <div style={S.qrWrap}><QRCodeSVG value={menuUrl} size={150} fgColor="#0a1408" bgColor="#e8f5ee" level="H" /></div>
                      <a href={menuUrl} target="_blank" rel="noreferrer" style={S.qrLink}>{trans('rest_open_menu')} →</a>
                      <div style={S.qrUrl}>{menuUrl}</div>
                    </div>
                  );
                })}
              </div>
            )
          )}
        </div>
      )}
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 11, color: 'var(--text3)', fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</label>
      {children}
    </div>
  );
}

const S = {
  header:       { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 28 },
  title:        { fontSize: 26, fontWeight: 800, color: 'var(--text)', marginBottom: 4 },
  sub:          { color: 'var(--text3)', fontSize: 14 },
  tabs:         { display: 'flex', gap: 8 },
  tab:          { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 8, padding: '8px 16px', color: 'var(--text3)', fontSize: 14, cursor: 'pointer' },
  tabActive:    { background: 'var(--primary)22', color: 'var(--primary)', borderColor: 'var(--primary)' },
  card:         { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 24, marginBottom: 20 },
  cardTitle:    { color: 'var(--text)', fontSize: 16, fontWeight: 700, marginBottom: 16 },
  row:          { display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 16, marginBottom: 16 },
  row3:         { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 16, marginBottom: 16 },
  row2:         { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16 },
  input:        { background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 12px', color: 'var(--text)', fontSize: 14, outline: 'none', width: '100%', boxSizing: 'border-box' },
  timeInput:    { background: 'var(--input-bg)', border: '1px solid var(--border)', borderRadius: 6, padding: '5px 8px', color: 'var(--text)', fontSize: 13, outline: 'none', colorScheme: 'dark' },
  btn:          { background: 'var(--primary)', color: 'white', border: 'none', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14, whiteSpace: 'nowrap' },
  cancelBtn:    { background: 'var(--card)', color: 'var(--text2)', border: '1px solid var(--border)', borderRadius: 8, padding: '10px 20px', fontWeight: 600, cursor: 'pointer', fontSize: 14 },
  center:       { textAlign: 'center', color: 'var(--text3)', padding: 40 },
  empty:        { textAlign: 'center', color: 'var(--text3)', padding: 60, background: 'var(--card)', borderRadius: 12, border: '1px dashed var(--border)' },
  list:         { display: 'flex', flexDirection: 'column', gap: 12 },
  item:         { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 12, padding: 16, display: 'flex', alignItems: 'center', gap: 14 },
  itemName:     { fontSize: 16, fontWeight: 700, color: 'var(--text)' },
  itemSub:      { fontSize: 13, color: 'var(--text2)' },
  cuisineTag:   { fontSize: 12, color: 'var(--text2)', background: 'var(--bg)', border: '1px solid var(--border)', padding: '2px 8px', borderRadius: 20 },
  priceTag:     { fontSize: 12, color: 'var(--warning)', background: 'var(--warning)15', padding: '2px 8px', borderRadius: 20, fontWeight: 700 },
  ratingTag:    { fontSize: 12, color: 'var(--success)', background: 'var(--success)15', padding: '2px 8px', borderRadius: 20 },
  actions:      { display: 'flex', gap: 8, flexShrink: 0, marginLeft: 'auto' },
  actionBtn:    { background: 'var(--primary)22', color: 'var(--primary)', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  editBtn:      { background: 'var(--warning)22', color: 'var(--warning)', border: 'none', borderRadius: 8, padding: '8px 16px', fontWeight: 600, cursor: 'pointer', fontSize: 13 },
  back:         { background: 'none', border: 'none', color: 'var(--primary)', cursor: 'pointer', fontSize: 14, marginBottom: 16, padding: 0 },
  secTitle:     { fontSize: 20, fontWeight: 700, color: 'var(--text)', marginBottom: 20 },
  catHead:      { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 },
  catName:      { fontSize: 16, fontWeight: 700, color: 'var(--text)' },
  catCount:     { fontSize: 12, color: 'var(--primary)', background: 'var(--primary)22', padding: '3px 10px', borderRadius: 12 },
  itemList:     { display: 'flex', flexDirection: 'column', gap: 8 },
  menuItem:     { display: 'flex', alignItems: 'center', gap: 12, padding: '10px 0', borderBottom: '1px solid var(--border)' },
  menuItemName: { fontSize: 14, fontWeight: 600, color: 'var(--text)' },
  menuItemDesc: { fontSize: 12, color: 'var(--text2)', marginTop: 2 },
  menuItemPrice:{ fontSize: 15, fontWeight: 700, color: 'var(--success)', flexShrink: 0 },
  iconBtn:      { background: 'none', border: 'none', cursor: 'pointer', fontSize: 14, padding: '4px 6px', borderRadius: 6, transition: 'background .15s' },
  delBtn:       { background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, padding: '3px 6px', color: 'var(--danger)', opacity: .6, borderRadius: 6 },
  tableGrid:    { display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(220px,1fr))', gap: 20 },
  tableCard:    { background: 'var(--card)', border: '1px solid var(--border)', borderRadius: 16, padding: 24, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 10 },
  tableNum:     { fontSize: 16, fontWeight: 700, color: 'var(--text)' },
  qrWrap:       { display: 'flex', justifyContent: 'center', padding: 12, background: '#e8f5ee', borderRadius: 12 },
  qrLink:       { display: 'block', color: 'var(--primary)', fontSize: 13, fontWeight: 600, textDecoration: 'none' },
  qrUrl:        { fontSize: 9, color: 'var(--text3)', wordBreak: 'break-all', maxWidth: 180 },
};
